import { useEffect, useState } from 'react';
import { useMap } from '@vis.gl/react-google-maps';

import useTheme from '@rover/kibble/themes/useTheme';
import type { RadiusUnit } from '@rover/react-lib/src/pages/provider-profile/types/hub/Radius';
import { RadiusUnits } from '@rover/react-lib/src/pages/provider-profile/types/hub/Radius';
import type { LatLng } from '@rover/types/src/LatLng';

export interface GeoMapCircleProps {
  center: LatLng | undefined;
  radius: number; // in meters
  strokeColor?: string;
  strokeOpacity?: number;
  strokeWeight?: number;
  fillColor?: string;
  fillOpacity?: number;
  clickable?: boolean;
  draggable?: boolean;
  editable?: boolean;
  visible?: boolean;
  zIndex?: number;
  onCenterChanged?: (center: LatLng) => void;
  unit: RadiusUnit;
}

const GeoMapCircle = ({
  center,
  radius,
  strokeColor,
  strokeOpacity = 1,
  strokeWeight = 2,
  fillColor,
  fillOpacity = 0.24,
  clickable = false,
  draggable = false,
  editable = false,
  visible = true,
  zIndex = 99,
  onCenterChanged,
  unit,
}: GeoMapCircleProps): null => {
  const [circle, setCircle] = useState<google.maps.Circle | null>(null);
  const map = useMap();
  const { colors } = useTheme();
  const resolvedStrokeColor = strokeColor || colors.green['400'];
  const resolvedFillColor = fillColor || colors.green['400'];
  const multiplier = unit === RadiusUnits.kilometers ? 1000 : 1609.34;

  // Create circle when map is available
  useEffect(() => {
    if (!map || !google || !center) return;

    const newCircle = new google.maps.Circle({
      map,
      center: {
        lat: parseFloat(center.lat.toString()),
        lng: parseFloat(center.lng.toString()),
      },
      radius: radius * multiplier,
      strokeColor: resolvedStrokeColor,
      strokeOpacity,
      strokeWeight,
      fillColor: resolvedFillColor,
      fillOpacity,
      clickable,
      draggable,
      editable,
      visible,
      zIndex,
    });

    setCircle(newCircle);

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [map, google]);

  // Update circle center
  useEffect(() => {
    if (!circle || !center) return;

    circle.setCenter({
      lat: parseFloat(center.lat.toString()),
      lng: parseFloat(center.lng.toString()),
    });
  }, [circle, center]);

  // Update circle radius
  useEffect(() => {
    if (!circle) return;
    circle.setRadius(radius * multiplier);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [circle, radius]);

  // Update circle style options
  useEffect(() => {
    if (!circle) return;

    circle.setOptions({
      strokeColor: resolvedStrokeColor,
      strokeOpacity,
      strokeWeight,
      fillColor: resolvedFillColor,
      fillOpacity,
      clickable,
      draggable,
      editable,
      visible,
      zIndex,
    });
  }, [
    circle,
    resolvedStrokeColor,
    strokeOpacity,
    strokeWeight,
    resolvedFillColor,
    fillOpacity,
    clickable,
    draggable,
    editable,
    visible,
    zIndex,
  ]);

  // Attach center changed listener
  useEffect(() => {
    if (!circle || !google || !onCenterChanged) return;

    const listener = google.maps.event.addListener(circle, 'center_changed', () => {
      const newCenter = circle.getCenter();
      if (newCenter) {
        onCenterChanged({
          lat: newCenter.lat(),
          lng: newCenter.lng(),
        });
      }
    });

    // eslint-disable-next-line consistent-return
    return () => {
      google.maps.event.removeListener(listener);
    };
  }, [circle, google, onCenterChanged]);

  return null;
};

export default GeoMapCircle;
